<template>
  <v-card v-if="groupMembers.length > 0">
    <v-card-title class="headline colorHeadlineStandard" primary-title>
      <div v-if="groupMembers.length === 1">
        {{ $tc('label.person') }}
      </div>
      <div v-else>
        {{ $tc('label.group') }}
      </div>
      <v-spacer />
      <v-icon large @click="$emit('update:openDialog', false)">{{ mdiClose }}</v-icon>
    </v-card-title>
    <v-card-text class="pa-3">
      <v-sheet height="300">
        <v-row>
          <v-col cols="6">
            <v-row>
              <v-col>
                <v-sheet height="160">
                  <v-row class="py-0">
                    <v-col cols="4">
                      <b>{{ $t('label.arrival') }}</b>
                    </v-col>
                    <v-col cols="4">
                      {{ formatDateTZ(groupMembers[0].timearrive, groupMembers[0].timezone, $store.state.auth.df.dateYearTime) }}
                    </v-col>
                  </v-row>
                  <v-row class="my-0">
                    <v-col cols="4">
                      <b>{{ $t('label.departure') }}</b>
                    </v-col>
                    <v-col cols="4">
                      {{ formatDateTZ(groupMembers[0].timedeparture, groupMembers[0].timezone, $store.state.auth.df.dateYearTime) }}
                    </v-col>
                  </v-row>
                </v-sheet>
              </v-col>
            </v-row>
            <v-form ref="formPlanVisit" v-model="validDates">
              <v-row>
                <v-col>
                  <b>{{ $t('label.planningNext') }}</b>
                </v-col>
              </v-row>
              <v-row class="my-0">
                <v-col cols="3" class="pt-4">
                  <b>{{ $t('label.arrival') }}</b>
                </v-col>
                <v-col cols="3">
                  <v-menu
                    v-model="dateEnterMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="dateEnterFormatted"
                        :label="$t('label.date')"
                        @blur="dateEnterBlur"
                        :rules="[ruleValidDate, dateGreaterThanPrevPlan]"
                        dense
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                        autocomplete="off"
                      />
                    </template>
                    <v-date-picker
                      :first-day-of-week="$store.state.auth.df.firstDayOfWeek"
                      v-model="dateEnter"
                      :min="groupMembers[0].timedeparture"
                      scrollable
                      no-title
                      @input="dateEnterMenu = false"
                      @change="dateEnterChange"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="3">
                  <vue-timepicker
                    v-model="timeEnter"
                    input-width="100%"
                    hide-clear-button
                    :minute-interval="5"
                    drop-direction="up"
                    advanced-keyboard
                    auto-scroll
                    manual-input
                    @change="timeEnterChange"
                  />
                </v-col>
                <v-col cols="3">
                  <vue-timepicker
                    v-model="timeExit"
                    input-width="100%"
                    hide-clear-button
                    :minute-interval="5"
                    :hourRange="exitHoursMin()"
                    :minuteRange="exitMinutesMin()"
                    drop-direction="up"
                    advanced-keyboard
                    auto-scroll
                    manual-input
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-col>
          <v-col cols="6">
            <div>
              <b v-if="groupMembers.length === 1">{{ $tc('label.person', 1) }}:</b>
              <b v-else>{{ $tc('label.person', 2) }}:</b>
            </div>
            <v-sheet height="270" class="overflow-y-auto">
              <div v-for="member in groupMembers" :key="member.id">
                <div>{{ member.name }} {{ member.surname }}</div>
              </div>
            </v-sheet>
          </v-col>
        </v-row>
      </v-sheet>
    </v-card-text>
    <v-divider />
    <v-card-actions>
      <v-btn color="colorSave" @click="plannedVisitsPost()">{{ $t('btn.add') }} {{ $t('label.planningNext') }}</v-btn>
      <v-spacer />
      <v-btn color="colorDelete" @click="dialogDelete = true">{{ $t('btn.delete') }}</v-btn>
      <v-spacer />
      <v-btn color="colorSave" @click="$emit('update:openDialog', false)">{{ $t('btn.close') }}</v-btn>
    </v-card-actions>

    <DialogConfirmDelete
      :header="$t('label.delete')"
      ::body="`${$t('msg.confirmDelete')}?`"
      :openDialog.sync="dialogDelete"
      v-on:confirm="plannedVisitsDelete"
    />
  </v-card>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import axios from 'axios';
import VueTimepicker from 'vue2-timepicker/src/vue-timepicker.vue';
import DialogConfirmDelete from '@/components/DialogConfirmDelete.vue';
// import moment from 'moment-timezone';

import { mdiClose } from '@mdi/js';

export default Vue.extend({
  name: 'PlanVisit',
  components: { VueTimepicker, DialogConfirmDelete },
  props: {
    groupMembers: Array as PropType<any[]> | [],
    openDialog: Boolean,
  },
  data: () => ({
    dateEnter: '',
    timeEnter: '08:00',
    timeExit: '09:00',
    dateEnterFormatted: '',
    initialDeparture: '', // pre kontrolu aby sa neprekryval cas prichodu opakovanej navstevy
    dateEnterMenu: false,
    validDates: false,
    dialogDelete: false,
    mdiClose: mdiClose,
  }),
  mounted() {
    this.onCardShow();
  },
  watch: {
    openDialog(newVal) {
      if (newVal) {
        this.onCardShow();
      }
    },
  },
  computed: {
    ruleValidDate() {
      return (v: any) => this.$moment(v, this.$store.state.auth.df.date, true).isValid() || this.$i18n.t('msg.invalidDateFormat');
    },
    dateGreaterThanPrevPlan() {
      return () =>
        this.$moment(this.groupMembers[0].timedeparture as Date)
          .subtract(1, 'days')
          .toDate() < new Date(this.dateEnter) || this.$i18n.t('msg.arrivalCannotBeInPast');
    },
  },
  methods: {
    onCardShow() {
      this.initialDeparture = { ...this.groupMembers[0].timedeparture };
      // Enter
      this.dateEnter = this.$moment(this.groupMembers[0].timearrive as Date)
        .add(1, 'days')
        .format('YYYY-MM-DD');
      this.dateEnterFormatted = this.$moment(this.dateEnter).format(this.$store.state.auth.df.date);
      this.timeEnter = this.$moment(this.groupMembers[0].timearrive as Date).format('HH:mm');
      this.timeExit = this.$moment(this.groupMembers[0].timedeparture as Date).format('HH:mm');
    },
    plannedVisitsPost() {
      let groupCopy = structuredClone(this.groupMembers);
      for (let member of groupCopy) {
        member.groupParentID === '' ? (member.groupParentID = member.groupID) : member.groupParentID;
        member.groupID = '';
        member.timearrive = this.$moment(this.dateEnter + '_' + this.timeEnter, 'YYYY-MM-DD_HH:mm')
          .utcOffset(0, true)
          .format();
        member.timedeparture = this.$moment(this.dateEnter + '_' + this.timeExit, 'YYYY-MM-DD_HH:mm')
          .utcOffset(0, true)
          .format();
      }
      axios
        .post(`/api/v1/plannedvisit`, { visitors: groupCopy, additionalDates: [] })
        .then(() => {
          this.$emit('update:openDialog', false);
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        })
        .finally(() => {
          groupCopy = [];
        });
    },
    plannedVisitsDelete() {
      this.dialogDelete = false;
      axios
        .delete(`/api/v1/plannedvisitgroup/${this.groupMembers[0].groupID}`)
        .then(() => {
          this.$emit('update:openDialog', false);
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
    dateEnterBlur() {
      this.dateEnter = this.momentParse(this.dateEnterFormatted);
      const form: any = this.$refs.formPlanVisit;
      form.validate();
    },
    dateEnterChange(date: string) {
      this.dateEnterFormatted = this.$moment(date).format(this.$store.state.auth.df.date);
    },
    momentParse(inputDate: string) {
      return this.$moment(inputDate, this.$store.state.auth.df.date, true).isValid()
        ? this.$moment(inputDate, this.$store.state.auth.df.date).format('YYYY-MM-DD')
        : '';
    },
    formatDateTZ(date: string, timeZone: string, format: string) {
      return this.$moment(date).tz(timeZone).format(format);
    },
    timeEnterChange() {
      if (this.$moment(this.timeEnter, 'HH:mm').add(60, 'minutes').format('DD') === this.$moment(this.timeEnter, 'HH:mm').format('DD')) {
        this.timeExit = this.$moment(this.timeEnter, 'HH:mm').add(60, 'minutes').format('HH:mm');
      } else {
        this.timeExit = this.$moment(this.timeEnter, 'HH:mm').format('HH:mm');
      }
    },
    exitHoursMin() {
      const currentHH = this.$moment(this.timeEnter, 'HH:mm').format('HH');
      const range = [];
      for (let i = parseInt(currentHH, 10); i <= 23; i++) {
        range.push(i);
      }
      return range;
    },
    exitMinutesMin() {
      const currentMM = this.$moment(this.timeEnter, 'HH:mm').format('mm');
      let startMM = 0;
      if (this.$moment(this.timeEnter, 'HH:mm').format('HH') === this.$moment(this.timeExit, 'HH:mm').format('HH')) {
        startMM = parseInt(currentMM, 10) + 5;
        if (this.$moment(this.timeExit, 'HH:mm').diff(this.$moment(this.timeEnter, 'HH:mm'), 'minutes') < 5) {
          this.timeExit = this.$moment(this.timeEnter, 'HH:mm').add(5, 'minutes').format('HH:mm');
        }
      } else {
        startMM = 0;
      }
      const range = [] as number[];
      for (let i = startMM; i <= 55; i += 5) {
        range.push(i);
      }
      return range;
    },
  },
});
</script>
